// i18n
import { translateInComponent, translate } from 'infras/i18n';
import translations from './i18n.json';

import { useRouter } from 'next/router';

import Grid from 'presentations/views/components/atoms/Grid';
import IntegrationListItem from 'presentations/views/components/molecules/IntegrationListItem';
import {
  IntegrationType,
  isAdminRequired,
} from 'domains/models/account/providerType';
import {
  calendarIntegrationsPath,
  hostApiKeyListPath,
  hostWebhookListPath,
  integrationPath,
  loginPath,
  webPath,
} from 'presentations/routes/path';
import { PlanId } from 'domains/models/subscription/planId';
import { IntegrationMergedTeamShopAccountItem } from 'presentations/api/schema/team_shop_account';
import { ViewerItem } from 'presentations/api/schema/user';
import { Role } from 'domains/models/team_user/role';
import { isAvailableSalesforce } from 'integrations/salesforce/presentations/api';
import Link from 'presentations/views/components/atoms/Link';
import styled from '@emotion/styled';
import { black } from 'presentations/views/styles/color';
import { useCallback, useMemo } from 'react';
import RoleRequiredDialog from '../RoleRequiredDialog';
import SearchEngineIntegrationFeature from 'domains/models/subscription/features/integrations/search_engine';
import AssistantFeature from 'domains/models/subscription/features/assistant';
import GoogleSheetIntegrationFeature from 'domains/models/subscription/features/integrations/google_sheet';
import MetaPixelIntegrationFeature from 'domains/models/subscription/features/integrations/meta_pixel';
import CustomTagIntegrationFeature from 'domains/models/subscription/features/integrations/custom_tag';

interface Props {
  viewer: ViewerItem;
  isLogin: boolean;
  selectedProviderTypes?: IntegrationType[];
  canAddCalendar?: boolean;
  shopAccount: IntegrationMergedTeamShopAccountItem | null;
  isiOSApp: boolean;
  isOpenDialog: boolean;
  onCloseDialog: any;
  checkAdminRole: any;
}

export const calendarIntegrationIds = [
  IntegrationType.GoogleCalendar,
  IntegrationType.Outlook,
  IntegrationType.iCloudCalendar,
  IntegrationType.GoogleMeet,
  IntegrationType.GoogleWorkspace,
];

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const router = useRouter();
  const selectedProviderTypes = props.selectedProviderTypes || [];

  const integrationTypesAdminRoleRequired = [
    IntegrationType.Stripe,
    IntegrationType.Salesforce,
    IntegrationType.HubSpot,
    IntegrationType.MetaPixel,
  ];
  const integrationTypes = [
    IntegrationType.GoogleCalendar,
    IntegrationType.Outlook,
    IntegrationType.iCloudCalendar,
    IntegrationType.Zoom,
    IntegrationType.GoogleMeet,
    IntegrationType.Slack,
    IntegrationType.Notion,
    IntegrationType.NotionForm,
    IntegrationType.GoogleSheet,
    IntegrationType.Stripe,
    IntegrationType.Teams,
  ];
  if (isAvailableSalesforce(props.viewer)) {
    integrationTypes.push(IntegrationType.Salesforce);
  }
  integrationTypes.push(IntegrationType.HubSpot);

  const isAvailableAssistant = useMemo(() => {
    if (!props.viewer.team?.id) return false;
    return AssistantFeature.isAvailableTeamId(props.viewer.team.id);
  }, [props.viewer.team?.id]);

  const searchEngineIntegrationFeature = useMemo(() => {
    return new SearchEngineIntegrationFeature({
      planVersion: props.viewer.planVersion,
    });
  }, [props.viewer.planVersion]);

  const metaPixelIntegrationFeature = useMemo(() => {
    return new MetaPixelIntegrationFeature({
      planVersion: props.viewer.planVersion,
    });
  }, [props.viewer.planVersion]);

  const customTagManagerIntegrationFeature = useMemo(() => {
    return new CustomTagIntegrationFeature({
      planVersion: props.viewer.planVersion,
    });
  }, [props.viewer.planVersion]);

  const onClickLink = useCallback(
    (e: any, integrationType: IntegrationType) => {
      if (props.isiOSApp && !calendarIntegrationIds.includes(integrationType)) {
        e.preventDefault();
        if (
          typeof window.webkit?.messageHandlers?.requestToOpenBrowser
            ?.postMessage === 'function'
        ) {
          const link = integrationPath(integrationType);
          window.webkit.messageHandlers.requestToOpenBrowser.postMessage(
            webPath(
              loginPath({ to: link, redirectIfLoggedIn: true }),
              router.locale,
            ),
          );
        }
      }

      if (integrationTypesAdminRoleRequired.includes(integrationType)) {
        props.checkAdminRole(e);
      }
    },
    [],
  );

  const isAvailableApiKey = true;

  return (
    <>
      <Grid container spacing={2}>
        {integrationTypes.map((integrationType) => {
          let isDisabled = false;
          let isConnect = false;
          if (integrationType === IntegrationType.Stripe) {
            isConnect = props.shopAccount?.isConnect || false;
            if (!props.viewer || props.viewer.role > Role.Admin) {
              isDisabled = true;
            }
          } else {
            isConnect = selectedProviderTypes.includes(integrationType);
            // disabledになり得るのは追加時
            if (!isConnect) {
              if (calendarIntegrationIds.includes(integrationType)) {
                if (!props.canAddCalendar) isDisabled = true;
              }
            }
          }

          if (
            integrationType === IntegrationType.GoogleSheet &&
            props.viewer.team?.id &&
            !GoogleSheetIntegrationFeature.isAvailableTeamId(
              props.viewer.team.id,
            )
          ) {
            return null;
          }

          const name = `IntegrationType_${integrationType}`;
          const subName = [
            IntegrationType.Notion,
            IntegrationType.NotionForm,
          ].includes(integrationType)
            ? t(`IntegrationType_${integrationType} subName`)
            : '';
          const link = calendarIntegrationIds.includes(integrationType)
            ? calendarIntegrationsPath()
            : integrationPath(integrationType);
          return (
            <Grid key={integrationType} item xs={12} tablet={4} desktop={4}>
              <StyledLink href={link} onClick={onClickLink}>
                <GridIntner>
                  <IntegrationListItem
                    name={translate(name)}
                    subName={subName}
                    image={t(`${name} image`)}
                    description={t(`${name} desc`)}
                    link={link}
                    onClickLink={onClickLink}
                    isDisabled={isDisabled}
                    isLogin={props.isLogin}
                    isConnect={isConnect}
                    integrationType={integrationType}
                    planId={props.viewer?.planId}
                    planVersion={props.viewer?.planVersion}
                    isAdminRequired={isAdminRequired(integrationType)}
                  />
                </GridIntner>
              </StyledLink>
            </Grid>
          );
        })}
        <Grid item xs={12} tablet={4} desktop={4}>
          <IntegrationListItem
            isDisabled={false}
            isLogin={props.isLogin}
            name={translate(
              `IntegrationType_${IntegrationType.GoogleAnalytics}`,
            )}
            description={t(
              `IntegrationType_${IntegrationType.GoogleAnalytics} desc`,
            )}
            image={t(
              `IntegrationType_${IntegrationType.GoogleAnalytics} image`,
            )}
          />
        </Grid>
        <Grid item xs={12} tablet={4} desktop={4}>
          <StyledLink
            href={integrationPath(IntegrationType.MetaPixel)}
            onClick={props.checkAdminRole}
          >
            <IntegrationListItem
              isLogin={props.isLogin}
              isAdminRequired
              name={translate(`IntegrationType_${IntegrationType.MetaPixel}`)}
              description={t(
                `IntegrationType_${IntegrationType.MetaPixel} desc`,
              )}
              image={t(`IntegrationType_${IntegrationType.MetaPixel} image`)}
              minimumPlanId={MetaPixelIntegrationFeature.currentPlanId}
              link={integrationPath(IntegrationType.MetaPixel)}
              isShowPricingLink={
                !metaPixelIntegrationFeature.isValid(props.viewer.planId)
              }
            />
          </StyledLink>
        </Grid>
        {/* <Grid item xs={12} tablet={4} desktop={4}>
          <StyledLink href={integrationPath(IntegrationType.CustomTag)} onClick={props.checkAdminRole}>
            <IntegrationListItem
              isLogin={props.isLogin}
              isAdminRequired
              name={translate(`IntegrationType_${IntegrationType.CustomTag}`)}
              description={t(`IntegrationType_${IntegrationType.CustomTag} desc`)}
              image={t(`IntegrationType_${IntegrationType.CustomTag} image`)}
              minimumPlanId={CustomTagIntegrationFeature.currentPlanId}
              isShowPricingLink={
                !customTagManagerIntegrationFeature.isValid(props.viewer.planId)
              }
            />
          </StyledLink>
        </Grid> */}
        <Grid item xs={12} tablet={4} desktop={4}>
          <StyledLink
            onClick={props.checkAdminRole}
            href={calendarIntegrationsPath({ resources: 1 })}
          >
            <IntegrationListItem
              isDisabled={false}
              isLogin={props.isLogin}
              minimumPlanId={PlanId.Team}
              isConnect={selectedProviderTypes.includes(
                IntegrationType.GoogleCalendar,
              )}
              name={translate(
                `IntegrationType_${IntegrationType.GoogleWorkspace}`,
              )}
              description={t(
                `IntegrationType_${IntegrationType.GoogleWorkspace} desc`,
              )}
              image={t(
                `IntegrationType_${IntegrationType.GoogleWorkspace} image`,
              )}
              link={calendarIntegrationsPath()}
            />
          </StyledLink>
        </Grid>
        <Grid item xs={12} tablet={4} desktop={4}>
          <IntegrationListItem
            minimumPlanId={PlanId.Enterprice}
            isShowPricingLink={true}
            isDisabled={false}
            isLogin={props.isLogin}
            name={translate(`IntegrationType_${IntegrationType.SMS}`)}
            description={t(`IntegrationType_${IntegrationType.SMS} desc`)}
            image={t(`IntegrationType_${IntegrationType.SMS} image`)}
            isAdminRequired
          />
        </Grid>

        <Grid item xs={12} tablet={4} desktop={4}>
          <StyledLink
            onClick={props.checkAdminRole}
            href={hostWebhookListPath()}
          >
            <IntegrationListItem
              isDisabled={false}
              minimumPlanId={PlanId.Team}
              isShowPricingLink={
                props.viewer?.planId && props.viewer?.planId < PlanId.Team
              }
              isLogin={props.isLogin}
              name={translate(`IntegrationType_${IntegrationType.Webhook}`)}
              description={t(`IntegrationType_${IntegrationType.Webhook} desc`)}
              image={t(`IntegrationType_${IntegrationType.Webhook} image`)}
              link={hostWebhookListPath()}
              onClickLink={props.checkAdminRole}
              isAdminRequired
            />
          </StyledLink>
        </Grid>
        {isAvailableApiKey && (
          <Grid item xs={12} tablet={4} desktop={4}>
            <StyledLink
              onClick={props.checkAdminRole}
              href={hostApiKeyListPath()}
            >
              <IntegrationListItem
                isDisabled={false}
                isShowPricingLink={
                  props.viewer?.planId && props.viewer?.planId < PlanId.Team
                }
                isLogin={props.isLogin}
                name={translate(`IntegrationType_${IntegrationType.Api}`)}
                description={t(`IntegrationType_${IntegrationType.Api} desc`)}
                image={t(`IntegrationType_${IntegrationType.Api} image`)}
                link={hostApiKeyListPath()}
                onClickLink={props.checkAdminRole}
                isAdminRequired
              />
            </StyledLink>
          </Grid>
        )}
        <Grid item xs={12} tablet={4} desktop={4}>
          <StyledLink
            onClick={props.checkAdminRole}
            href={integrationPath(IntegrationType.SearchEngine)}
          >
            <IntegrationListItem
              isDisabled={false}
              minimumPlanId={SearchEngineIntegrationFeature.currentPlanId}
              isShowPricingLink={
                !searchEngineIntegrationFeature.isValid(props.viewer.planId)
              }
              isLogin={props.isLogin}
              name={translate(
                `IntegrationType_${IntegrationType.SearchEngine}`,
              )}
              description={t(
                `IntegrationType_${IntegrationType.SearchEngine} desc`,
              )}
              image={t(`IntegrationType_${IntegrationType.SearchEngine} image`)}
              link={integrationPath(IntegrationType.SearchEngine)}
              onClickLink={props.checkAdminRole}
              isAdminRequired
            />
          </StyledLink>
        </Grid>
        {isAvailableAssistant && (
          <Grid item xs={12} tablet={4} desktop={4}>
            <StyledLink
              onClick={props.checkAdminRole}
              href={integrationPath(IntegrationType.Assistant)}
            >
              <IntegrationListItem
                isDisabled={false}
                minimumPlanId={SearchEngineIntegrationFeature.currentPlanId}
                isShowPricingLink={
                  !searchEngineIntegrationFeature.isValid(props.viewer.planId)
                }
                isLogin={props.isLogin}
                name={translate(`IntegrationType_${IntegrationType.Assistant}`)}
                description={t(
                  `IntegrationType_${IntegrationType.Assistant} desc`,
                )}
                image={t(`IntegrationType_${IntegrationType.Assistant} image`)}
                link={integrationPath(IntegrationType.Assistant)}
                onClickLink={props.checkAdminRole}
                isAdminRequired
              />
            </StyledLink>
          </Grid>
        )}
        <Grid item xs={12} tablet={4} desktop={4}>
          <IntegrationListItem
            minimumPlanId={PlanId.Enterprice}
            isShowPricingLink={true}
            isDisabled={false}
            isLogin={props.isLogin}
            name={translate(`IntegrationType_${IntegrationType.LinkAccount}`)}
            description={t(
              `IntegrationType_${IntegrationType.LinkAccount} desc`,
            )}
            image={t(`IntegrationType_${IntegrationType.LinkAccount} image`)}
            isAdminRequired
          />
        </Grid>
      </Grid>
      {props.isOpenDialog && (
        <RoleRequiredDialog onClose={props.onCloseDialog} />
      )}
    </>
  );
}

const GridIntner = styled.span`
  cursor: pointer;
`;
const StyledLink = styled(Link)`
  color: ${black};
`;
